import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { appPath } from '../utils/appRouting';
import { isNativeApp } from '../utils/isNativeApp';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Loader2, 
  AlertCircle, 
  ArrowLeft, 
  Mail, 
  Lock,
  Eye,
  EyeOff,
  Shield,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { auth } from '../firebase';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  clearPendingGoogleCredential,
  extractPendingFromError,
  isAccountExistsDifferentCredentialError,
  loadPendingGoogleCredential,
  linkPendingCredentialToUser,
  savePendingGoogleCredential,
  signInWithGoogle
} from '../auth/googleAuth';

const GoogleG = (props) => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" {...props}>
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.675 32.659 29.226 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4c-7.682 0-14.337 4.327-17.694 10.691z"/>
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.252 35.091 26.761 36 24 36c-5.205 0-9.64-3.318-11.287-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.06 12.06 0 0 1-4.084 5.57l.003-.002 6.19 5.238C36.971 39.23 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const { login, refreshUserProfile, error: authError } = useAuth();
  const [loading, setLoading] = useState(false);
  const isDev = process.env.NODE_ENV !== 'production';
  const isApp = isNativeApp();
  const [showPassword, setShowPassword] = useState(false);
  const loginSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(1, 'Inserisci la tua email')
          .email('Inserisci un\'email valida')
          .transform((v) => v.trim()),
        password: z.string().min(1, 'Inserisci la password'),
      }),
    []
  );

  const pending = useMemo(() => loadPendingGoogleCredential(), []);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: pending?.email || '',
      password: '',
    },
  });

  useEffect(() => {
    if (pending?.email) {
      setValue('email', pending.email, { shouldValidate: true });
    }
  }, [pending?.email, setValue]);

  const authLog = (...args) => {
    if (!isDev) return;
    // eslint-disable-next-line no-console
    console.log(...args);
  };

  const withTimeout = (promise, ms) => {
    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        const err = new Error('LOGIN_TIMEOUT');
        err.code = 'LOGIN_TIMEOUT';
        reject(err);
      }, ms);
    });

    return Promise.race([promise, timeoutPromise]).finally(() => {
      clearTimeout(timeoutId);
    });
  };

  const onSubmit = async (values) => {
    if (loading) return;
    setLoading(true);

    authLog('[AUTH] start login');

    try {
      authLog('[AUTH] before signIn');
      const result = await withTimeout(login(values.email, values.password), 15000);
      
      // Controlla se l'email è verificata
      if (!result.emailVerified) {
        toast.info('Verifica la tua email per accedere');
        navigate(appPath('/verify-email'));
        return;
      }

      // Se c'è una credenziale Google in sospeso (email già registrata), prova a collegarla ora.
      const pendingNow = loadPendingGoogleCredential();
      const normalizedEmail = String(values.email || '').trim().toLowerCase();
      if (pendingNow?.email && String(pendingNow.email).trim().toLowerCase() === normalizedEmail) {
        try {
          await linkPendingCredentialToUser(result.user);
          toast.success('Account Google collegato');
          // Refresh profilo backend (providers/profileComplete)
          try {
            await refreshUserProfile?.();
          } catch {
            // ignore
          }
        } catch (linkErr) {
          console.error('Errore link Google:', linkErr);
          toast.error('Impossibile collegare Google. Riprova dal profilo.');
        } finally {
          clearPendingGoogleCredential();
        }
      }
      
      toast.success('Accesso effettuato con successo!');
      navigate(appPath('/dashboard'));
    } catch (error) {
      authLog('[AUTH] error', error);
      console.error('Errore login:', error);
      if (error?.code === 'LOGIN_TIMEOUT') {
        toast.error('Impossibile effettuare l\'accesso. Controlla la connessione e riprova.');
        return;
      }
      if (error.code === 'auth/user-not-found') {
        toast.error('Utente non trovato');
      } else if (error.code === 'auth/wrong-password') {
        toast.error('Password non corretta');
      } else if (error.code === 'auth/invalid-credential') {
        // Firebase Auth v9+ unifica wrong-password e user-not-found in invalid-credential
        toast.error('Email o password non corretti');
      } else {
        toast.error('Credenziali non valide');
      }
    } finally {
      authLog('[AUTH] finally stop loading');
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await signInWithGoogle(auth);
      // onAuthStateChanged gestisce il resto.
      toast.success('Accesso con Google effettuato');
      navigate(appPath('/dashboard'));
    } catch (err) {
      console.error('Google login error:', err);
      if (isAccountExistsDifferentCredentialError(err)) {
        const pendingData = extractPendingFromError(err);
        savePendingGoogleCredential(pendingData);
        toast.info('Esiste già un account con questa email. Collega Google per continuare.');
        navigate(appPath('/link-account'));
        return;
      }
      if (err?.code === 'auth/popup-closed-by-user') {
        toast.info('Accesso annullato');
        return;
      }
      toast.error('Errore accesso con Google');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-[100dvh] overflow-y-auto bg-gradient-to-br from-muted/30 via-background to-muted/30 text-foreground flex">
      {/* Left Panel - Branding (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-brand to-brand-2 p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <span className="text-yellow-400 font-bold text-xl">S+</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">SOSTEGNO PRO+</h1>
              <p className="text-white/70 text-sm">Piattaforma digitale per docenti</p>
            </div>
          </div>
          
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-white mb-4">
                Benvenuto in SOSTEGNO PRO+
              </h2>
              <p className="text-white/80 text-lg">
                Un unico spazio per docenti di sostegno e curricolari: documenti, organizzazione, materiali e collaborazione.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="w-10 h-10 bg-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-blue-900" />
                </div>
                <div>
                  <p className="text-white font-medium">Sicurezza e GDPR</p>
                  <p className="text-white/70 text-sm">Privacy-by-design e controlli di accesso</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="w-10 h-10 bg-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-blue-900" />
                </div>
                <div>
                  <p className="text-white font-medium">Strumenti concreti</p>
                  <p className="text-white/70 text-sm">Documentazione, AI didattica, archivio e community</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/15">
                  <p className="text-white font-medium">Documenti</p>
                  <p className="text-white/70 text-sm">PEI, PDP e verbali</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/15">
                  <p className="text-white font-medium">AI didattica</p>
                  <p className="text-white/70 text-sm">Materiali e adattamenti</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/15">
                  <p className="text-white font-medium">Classi e alunni</p>
                  <p className="text-white/70 text-sm">Organizzazione semplice</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/15">
                  <p className="text-white font-medium">Community</p>
                  <p className="text-white/70 text-sm">Condivisione e confronto</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-white/70 text-sm">
          © 2025 SOSTEGNO PRO+ - Tutti i diritti riservati
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Header action (web: back home) / (app: register) */}
          {!isApp ? (
            <button
              onClick={() => navigate(appPath('/'))}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Torna alla home</span>
            </button>
          ) : (
            <div className="mb-8">
              <Link
                to={appPath('/register')}
                className="inline-flex items-center gap-2 rounded-full bg-card text-foreground px-4 py-2 shadow-sm border border-border hover:bg-muted transition-colors"
              >
                <span className="text-sm font-medium">Registrati</span>
              </Link>
            </div>
          )}

          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-brand to-brand-2 rounded-2xl flex items-center justify-center shadow-elev1">
                <span className="text-yellow-400 font-bold text-2xl">S+</span>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-foreground">SOSTEGNO PRO+</h1>
            <p className="text-muted-foreground text-sm mt-1">Accesso per docenti di sostegno e curricolari</p>
          </div>

          {/* Login Card */}
          <div className="bg-card rounded-2xl shadow-xl border border-border p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-foreground">Accedi al tuo account</h2>
              <p className="text-muted-foreground mt-2">Inserisci le tue credenziali per continuare</p>
              <div className="mt-3 text-xs text-muted-foreground">
                Piattaforma per docenti • Sicurezza e GDPR
              </div>
            </div>

            {authError && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{authError}</AlertDescription>
              </Alert>
            )}

            {pending?.email ? (
              <Alert className="mb-6">
                <AlertDescription>
                  Esiste già un account con questa email. Accedi con password per collegare Google.
                </AlertDescription>
              </Alert>
            ) : null}

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 mb-4"
              onClick={handleGoogle}
              disabled={loading}
            >
              <GoogleG className="mr-2" />
              Continua con Google
            </Button>

            <div className="flex items-center gap-3 mb-4">
              <div className="h-px bg-border flex-1" />
              <div className="text-xs text-muted-foreground">oppure</div>
              <div className="h-px bg-border flex-1" />
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-foreground">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tuo.email@scuola.it"
                    {...register('email')}
                    disabled={loading}
                    className="pl-10 h-12"
                    data-testid="email-input"
                    aria-invalid={Boolean(errors.email)}
                  />
                </div>
                {errors.email?.message ? (
                  <p className="text-xs text-destructive mt-1">{String(errors.email.message)}</p>
                ) : null}
              </div>

              <div>
                <Label htmlFor="password" className="text-foreground">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    {...register('password')}
                    disabled={loading}
                    className="pl-10 pr-10 h-12"
                    data-testid="password-input"
                    aria-invalid={Boolean(errors.password)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password?.message ? (
                  <p className="text-xs text-destructive mt-1">{String(errors.password.message)}</p>
                ) : null}
              </div>

              <Button
                type="submit"
                className="w-full h-12 ds-btn-gradient"
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Accesso in corso...
                  </>
                ) : (
                  'Accedi'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Problemi di accesso? Contattaci a support@sostegnopro.it
              </p>
            </div>
          </div>

          {/* Security badges */}
          <div className="mt-8 flex items-center justify-center gap-6 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Shield className="w-4 h-4" />
              <span>GDPR Compliant</span>
            </div>
            <div className="flex items-center gap-1">
              <Lock className="w-4 h-4" />
              <span>Connessione sicura</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
